import { useState } from 'react';
import {
  Alert,
  Anchor,
  Badge,
  Button,
  FileInput,
  Group,
  Select,
  Stack,
  Switch,
  Table,
  Text,
  TextInput,
  Textarea,
  Title,
} from '@mantine/core';
import { Link, useNavigate } from 'react-router';
import {
  AKSIYON_DEVIR_SONUC_ETIKETI,
  AKSIYON_DURUM_ETIKETI,
  TENANT_ANAHTAR_ETIKETLERI,
} from '@isg/validation';
import {
  AKSIYON_SUZGEC_BOS,
  aksiyonSuzgecSayisi,
  aksiyonSuzgecSorgusu,
  useAksiyonAc,
  useAksiyonlar,
  useAtanabilirler,
  useIkincilOkuma,
  useCiktiIndir,
  useDevirYukle,
  useLookup,
  useTenantSettings,
  useTenantSettingYaz,
  useWorkplacesTumu,
  useKapilar,
} from '../api/queries';
import type { AksiyonSuzgeci } from '../api/queries';
import { AksiyonTablosu } from './aksiyon-parts';
import { BosListeNotu, QueryStatus } from './parts';
import { Sayfalama, SuzgecGostergesi, KapiSebebi } from '../sozlesme';
import { gunGirdisiIso } from '../lib/format';
import { ANLAM } from '../theme';

/**
 * Aksiyon listesi + açma formu (F2·S2 — Kapsam §22.3). Görünürlük role göre
 * sadeleşir, karar sunucuda (§2.16):
 *
 * - "Yeni aksiyon" düğmesi atama yetkisi olan şapkalarda; uzman, tenant ayarı
 *   (`uzman_aksiyon_atama`) kapalıyken düğmeyi görmez — API'si de 403'ler
 *   (uzmanın talep yolu Talepler ekranında her durumda açık).
 * - Uzman atama ayarını yalnız holding yöneticisi değiştirir (buradaki
 *   anahtar — ilk tenant ayarı).
 */
export function AksiyonlarScreen(): React.JSX.Element {
  // P13-b (madde 225): liste düzeyi kapılar SUNUCUDAN (`GET kapilar`) — ekran şapka
  // dizgesi yazmaz; aksiyon kapısı `uzman_aksiyon_atama` anahtarını da sunucuda okur.
  const kapilar = useKapilar();
  const acabilir = kapilar.data?.aksiyonKapisi.yazabilir ?? false;
  const holdingYoneticisi = kapilar.data?.aksiyonAyarKapisi.yazabilir ?? false;
  const devirYukleyebilir = kapilar.data?.aksiyonDevirKapisi.yazabilir ?? false;
  const [suzgec, setSuzgec] = useState<AksiyonSuzgeci>(AKSIYON_SUZGEC_BOS);
  const [devirAcik, setDevirAcik] = useState(false);
  const aksiyonlar = useAksiyonlar(suzgec);
  // Ayar yalnız holding yöneticisinin anahtarı için okunur — başka şapkada
  // uç ÇAĞRILMAZ (sessiz 403 paketi: teknisyende 403 dönüyordu).
  const ayarlar = useTenantSettings({ enabled: holdingYoneticisi });
  const ayarYaz = useTenantSettingYaz();
  const atanabilirOkuma = useIkincilOkuma('atanabilir');
  const kaynaklar = useLookup('aksiyon_kaynagi');
  const atanabilirler = useAtanabilirler();
  const indir = useCiktiIndir();

  // Süzgeç değişince İLK SAYFAYA dön: üçüncü sayfadayken süzen kullanıcı
  // aksi hâlde boş bir sayfa görür ve "kayıt yok" sanır.
  const suzgecDegis = (yama: Partial<AksiyonSuzgeci>): void =>
    setSuzgec((mevcut) => ({ ...mevcut, ...yama, sayfa: 1 }));

  return (
    <Stack>
      <Group justify="space-between">
        <Title order={3}>Aksiyonlar</Title>
        <Group>
          {holdingYoneticisi && ayarlar.isSuccess && (
            /* Etiket TEK YERDEN (F4·S2 — Kapsam §23/1): aynı anahtar
               yapılandırma ekranında da bu adla görünür; ekrana ikinci bir
               metin yazmak iki yüzeyde iki farklı ad demekti. */
            <Switch
              data-testid="uzman-atama-ayari"
              label={TENANT_ANAHTAR_ETIKETLERI.uzman_aksiyon_atama}
              checked={ayarlar.data.uzmanAksiyonAtama}
              onChange={(event) =>
                ayarYaz.mutate({
                  anahtar: 'uzman_aksiyon_atama',
                  deger: event.currentTarget.checked,
                })
              }
            />
          )}
          {/* T7 — ÇIKTI EKRANIN SÜZGECİNİ KOŞAR: aynı sorgu dizesi sunucuya
              gider ("312 kayıt" ne diyorsa dosyada o kadar satır olur).
              Yetki = okuma yetkisi: bu ekranı gören çıktısını alır. */}
          <Button
            variant="default"
            data-testid="aksiyon-excel"
            loading={indir.isPending}
            onClick={() =>
              indir.mutate({
                yol: `/api/actions/export?${aksiyonSuzgecSorgusu(suzgec).toString()}`,
                yedekAd: 'aksiyonlar.xlsx',
              })
            }
          >
            Excel'e aktar
          </Button>
          {/* A1 — DEVİR YÜKLEMESİ YALNIZ HOLDİNG YÖNETİCİSİNDE (kurulum
              işi; §20.5 satırı). Menüde satır AÇILMADI (T5/T8): devir bir
              yer değil, bu sayfanın bir işlemidir. */}
          {devirYukleyebilir && (
            <Button
              variant="default"
              data-testid="devir-panel-ac"
              onClick={() => setDevirAcik((acik) => !acik)}
            >
              {devirAcik ? 'Devir panelini kapat' : 'Sistem öncesi kayıtları yükle'}
            </Button>
          )}
          {acabilir && (
            <Button component={Link} to="/aksiyonlar/yeni" data-testid="aksiyon-ac">
              Yeni aksiyon
            </Button>
          )}
        </Group>
      </Group>
      {/* Sebep kapının KENDİ cümlesi — İSG ekibi anahtarı kapalıysa sunucunun metni
          ("… bu holdingde kapalı — Uygunsuzluklar ekranından bulgu bildirebilirsiniz"). */}
      {kapilar.data && (
        <KapiSebebi
          durum={kapilar.data.aksiyonKapisi}
          nesne="Aksiyonu"
          fiil="açar"
          testId="uzman-atama-kapali-notu"
        />
      )}
      {kapilar.data && (
        <KapiSebebi
          durum={kapilar.data.aksiyonAyarKapisi}
          nesne="İSG ekibi ayarını"
          fiil="değiştirir"
          testId="aksiyon-ayar-yazamama-sebebi"
        />
      )}
      {kapilar.data && (
        <KapiSebebi
          durum={kapilar.data.aksiyonDevirKapisi}
          nesne="Sistem öncesi kayıtları"
          fiil="yükler"
          testId="aksiyon-devir-yazamama-sebebi"
        />
      )}

      {devirYukleyebilir && devirAcik && <DevirYuklemePaneli />}

      {/* P4 — SÜZGEÇ SUNUCUDA KOŞAR, GÖSTERGE GÖRÜNÜR DURUR (T5). */}
      <Group align="flex-end" gap="sm">
        <Select
          label="Durum"
          data-testid="aksiyon-suzgec-durum"
          w={190}
          data={[
            { value: 'hepsi', label: 'Tümü' },
            { value: 'open', label: AKSIYON_DURUM_ETIKETI.open },
            { value: 'completed_reported', label: AKSIYON_DURUM_ETIKETI.completed_reported },
            { value: 'closed', label: AKSIYON_DURUM_ETIKETI.closed },
            // ⚠️ Dördüncü token 2½b'de eklendi ama süzgece girmemişti: iptal
            // edilen kayıtlar listede görünüyor, AYIKLANAMIYORDU.
            { value: 'cancelled', label: AKSIYON_DURUM_ETIKETI.cancelled },
          ]}
          value={suzgec.durum}
          onChange={(deger) =>
            suzgecDegis({ durum: (deger ?? 'hepsi') as AksiyonSuzgeci['durum'] })
          }
        />
        <Select
          label="Kaynak"
          data-testid="aksiyon-suzgec-kaynak"
          w={220}
          clearable
          searchable
          data={(kaynaklar.data ?? []).map((kalem) => ({ value: kalem.id, label: kalem.ad }))}
          value={suzgec.kaynakTipiId}
          onChange={(deger) => suzgecDegis({ kaynakTipiId: deger })}
        />
        {/* Atanabilir havuzu okuyamayan şapkada (izleyici) süzgeç ÇİZİLMEZ ve uç
            çağrılmaz — boş bir açılır liste "kimse yok" derdi (sessiz 403 paketi). */}
        {atanabilirOkuma.acik && (
          <Select
            label="Atanan"
            data-testid="aksiyon-suzgec-atanan"
            w={220}
            clearable
            searchable
            data={(atanabilirler.data ?? []).map((kisi) => ({
              value: kisi.appUserId,
              label: kisi.etiket,
            }))}
            value={suzgec.atananAppUserId}
            onChange={(deger) => suzgecDegis({ atananAppUserId: deger })}
          />
        )}
        <Switch
          label="Yalnız gecikmiş"
          data-testid="aksiyon-suzgec-gecikmis"
          checked={suzgec.gecikmis}
          onChange={(event) => suzgecDegis({ gecikmis: event.currentTarget.checked })}
        />
        <SuzgecGostergesi
          etkinSayisi={aksiyonSuzgecSayisi(suzgec)}
          onTemizle={() => setSuzgec(AKSIYON_SUZGEC_BOS)}
          testId="aksiyon-suzgec-gostergesi"
        />
      </Group>

      <QueryStatus queries={[aksiyonlar]} />
      {aksiyonlar.isSuccess && aksiyonlar.data.toplam === 0 && (
        <BosListeNotu
          mesaj={
            aksiyonSuzgecSayisi(suzgec) > 0
              ? 'Bu süzgeçle eşleşen aksiyon yok.'
              : 'Bahçenizde görüntülenecek aksiyon yok.'
          }
        />
      )}
      {aksiyonlar.isSuccess && aksiyonlar.data.toplam > 0 && (
        <>
          <AksiyonTablosu rows={aksiyonlar.data.satirlar} testId="aksiyon-listesi" />
          <Sayfalama
            sayfa={aksiyonlar.data.sayfa}
            sayfaBoyutu={aksiyonlar.data.sayfaBoyutu}
            toplam={aksiyonlar.data.toplam}
            onDegis={(sayfa) => setSuzgec((mevcut) => ({ ...mevcut, sayfa }))}
            testId="aksiyon-sayfalama"
          />
        </>
      )}
    </Stack>
  );
}

/**
 * Aksiyon açma formu — talep dönüştürme modalıyla aynı alan seti (workplace
 * hariç; orada işyeri talepten gelir). Termin ZORUNLU (Kapsam §22.3/6).
 */
export function AksiyonFormAlanlari({
  atanan,
  setAtanan,
  kaynak,
  setKaynak,
  baslik,
  setBaslik,
  termin,
  setTermin,
  aciklama,
  setAciklama,
  referansNotu,
  setReferansNotu,
  kaynakTuretilir = false,
}: {
  atanan: string | null;
  setAtanan: (value: string | null) => void;
  kaynak: string | null;
  setKaynak: (value: string | null) => void;
  baslik: string;
  setBaslik: (value: string) => void;
  termin: string;
  setTermin: (value: string) => void;
  aciklama: string;
  setAciklama: (value: string) => void;
  referansNotu: string;
  setReferansNotu: (value: string) => void;
  /**
   * T6/T13 — TİPLİ BAĞDAN AÇILAN DÖF'te kaynak SEÇTİRİLMEZ. Sunucu kaynağı
   * kayıttan türetiyor (`kaynakTipiniCoz`); seçim alanını açık bırakmak,
   * kullanıcının seçtiği değerin SESSİZCE yok sayıldığı bir kontrol demekti
   * — §2.40'ın "yalan söyleyen kontrol" sınıfının tam tanımı.
   */
  kaynakTuretilir?: boolean;
}): React.JSX.Element {
  const atanabilirler = useAtanabilirler();
  const kaynaklar = useLookup('aksiyon_kaynagi');
  return (
    <>
      <TextInput
        label="Başlık"
        required
        data-testid="aksiyon-baslik"
        value={baslik}
        onChange={(event) => setBaslik(event.currentTarget.value)}
      />
      {kaynakTuretilir ? (
        <Text size="sm" c="dimmed" data-testid="aksiyon-kaynak-turetildi">
          Kaynak, DÖF'ün açıldığı kayıttan otomatik belirlenir.
        </Text>
      ) : (
        <Select
          label="Kaynak"
          required
          data-testid="aksiyon-kaynak"
          searchable
          data={(kaynaklar.data ?? [])
            .filter((kalem) => !kalem.gizli)
            .map((kalem) => ({ value: kalem.id, label: kalem.ad }))}
          value={kaynak}
          onChange={setKaynak}
        />
      )}
      <TextInput
        label="Kaynak referans notu (opsiyonel)"
        data-testid="aksiyon-referans-notu"
        value={referansNotu}
        onChange={(event) => setReferansNotu(event.currentTarget.value)}
      />
      <Select
        label="Atanan (kişi bağlı aktif hesaplar)"
        required
        data-testid="aksiyon-atanan"
        searchable
        data={(atanabilirler.data ?? []).map((kisi) => ({
          value: kisi.appUserId,
          label: kisi.etiket,
        }))}
        value={atanan}
        onChange={setAtanan}
      />
      {/* ✏️ T13 (madde 137 deseni): ELLE ISO YAZDIRAN TextInput ÖLDÜ.
          Kullanıcı "15.09.2026" yazınca alan sessizce geçersiz kalıyor ve
          düğme hiçbir açıklama vermeden kapalı duruyordu. Takvim seçici +
          `gunGirdisiIso` doğrulaması — form ekranlarıyla TEK DESEN. */}
      <TextInput
        label="Termin"
        required
        type="date"
        data-testid="aksiyon-termin"
        value={termin}
        onChange={(event) => setTermin(event.currentTarget.value)}
      />
      <Textarea
        label="Açıklama (opsiyonel)"
        data-testid="aksiyon-aciklama"
        value={aciklama}
        onChange={(event) => setAciklama(event.currentTarget.value)}
      />
    </>
  );
}

/**
 * YENİ AKSİYON — AYRI SAYFA (T13 · P1).
 *
 * ⚠️ Aksiyon bir **BAĞIMSIZ KAYITTIR** (kendi sayfası, kendi URL'i, kendi
 * ekleri var) — sözleşmenin yapısal ayrımına göre MODALDA açılmaz. Modal
 * yalnız onay sorularına kalır (§20.3b/P1). Ayrıca modal, formda hata alan
 * kullanıcıyı listeye geri fırlatıyor ve girdisini kaybettiriyordu.
 */
export function AksiyonYeniScreen(): React.JSX.Element {
  const navigate = useNavigate();
  // ✏️ TUR 9 / B4 (T9-18): şirket başına fan-out yerine TEK istek.
  const workplaces = useWorkplacesTumu().data ?? [];
  const ac = useAksiyonAc();

  const [workplaceId, setWorkplaceId] = useState<string | null>(null);
  const [atanan, setAtanan] = useState<string | null>(null);
  const [kaynak, setKaynak] = useState<string | null>(null);
  const [baslik, setBaslik] = useState('');
  const [termin, setTermin] = useState('');
  const [aciklama, setAciklama] = useState('');
  const [referansNotu, setReferansNotu] = useState('');

  // §2.40 — geçerlilik GÖNDERİM ÖNCESİ çözülür; geçersiz tarihte düğme
  // kapalıdır ve SEBEBİ yazılıdır (sessizce düşen düğme yok).
  const terminGecerli = gunGirdisiIso(termin) !== null;
  const terminHatasi = termin.trim() !== '' && !terminGecerli;
  const hazir =
    Boolean(workplaceId) &&
    Boolean(atanan) &&
    Boolean(kaynak) &&
    baslik.trim() !== '' &&
    terminGecerli;

  return (
    <Stack maw={640}>
      <Anchor component={Link} to="/aksiyonlar" size="sm" data-testid="aksiyon-yeni-geri">
        ← Aksiyonlara dön
      </Anchor>
      <Title order={3}>Yeni aksiyon</Title>
      <Select
        label="İşyeri"
        required
        data-testid="aksiyon-isyeri"
        searchable
        data={workplaces
          .filter((isyeri) => isyeri.durum === 'active')
          .map((isyeri) => ({ value: isyeri.id, label: isyeri.ad }))}
        value={workplaceId}
        onChange={setWorkplaceId}
      />
      <AksiyonFormAlanlari
        atanan={atanan}
        setAtanan={setAtanan}
        kaynak={kaynak}
        setKaynak={setKaynak}
        baslik={baslik}
        setBaslik={setBaslik}
        termin={termin}
        setTermin={setTermin}
        aciklama={aciklama}
        setAciklama={setAciklama}
        referansNotu={referansNotu}
        setReferansNotu={setReferansNotu}
      />
      {terminHatasi && (
        <Text size="sm" c={ANLAM.engel} role="alert" data-testid="aksiyon-termin-hatasi">
          Geçerli bir termin tarihi seçin.
        </Text>
      )}
      {ac.isError && (
        <Text size="sm" c={ANLAM.engel} role="alert" data-testid="aksiyon-ac-hata">
          {ac.error.message}
        </Text>
      )}
      <Button
        data-testid="aksiyon-ac-onayla"
        disabled={!hazir}
        loading={ac.isPending}
        onClick={() => {
          if (!hazir || !workplaceId || !atanan || !kaynak) return;
          ac.mutate(
            {
              workplaceId,
              atananAppUserId: atanan,
              kaynakTipiId: kaynak,
              baslik: baslik.trim(),
              terminTarihi: termin,
              aciklama: aciklama.trim() === '' ? null : aciklama.trim(),
              kaynakReferansNotu: referansNotu.trim() === '' ? null : referansNotu.trim(),
            },
            // Açılan kaydın KENDİ sayfasına gidilir — kullanıcı ne
            // oluşturduğunu görür (modal kapanıp listeye dönmek, kaydın
            // gerçekten açılıp açılmadığını aratıyordu).
            { onSuccess: (acilan) => void navigate(`/aksiyonlar/${acilan.id}`) },
          );
        }}
      >
        Aksiyonu aç ve ata
      </Button>
    </Stack>
  );
}

/**
 * SİSTEM ÖNCESİ KAYITLARIN TOPLU DEVRİ (2½-sonrası — A1/A7).
 *
 * `RiskMaddeYuklemePaneli` deseninin devir kolu: **önizleme YOK** (satır
 * kişi dosyası açmaz, yanlış satır tek tıkla iptal edilir), rapor satır
 * numarası + sebep + UYARI taşır.
 *
 * ⚠️ **UYARI ≠ HATA ve panel bunu görünür kılar.** Satır işlendiği hâlde
 * beyanla sistem arasında bir fark oluştuysa (atayan ikame edildi, sorumlu
 * ad olarak saklandı) satır YEŞİL değil, uyarı rengiyle listelenir —
 * kullanıcı neyin beyan, neyin veri olduğunu dosyayı kapatmadan görür.
 */
function DevirYuklemePaneli(): React.JSX.Element {
  const yukle = useDevirYukle();
  const indir = useCiktiIndir();
  const [dosya, setDosya] = useState<File | null>(null);
  return (
    <Stack gap="sm" data-testid="devir-paneli">
      <Text size="sm" c="dimmed">
        Sisteme geçmeden önce tutulan DÖF kayıtlarını yükler. Kapanış tarihi dolu satırlar
        <strong> kapalı </strong>olarak açılır ve kapanışları <strong>müşteri beyanı</strong>{' '}
        sayılır — sistem onay bilgisi aramaz. Her kayıt listede “Devir kaydı” rozetiyle görünür.
      </Text>
      <Group align="flex-end">
        <Button
          variant="default"
          size="xs"
          data-testid="devir-sablon-indir"
          loading={indir.isPending}
          onClick={() =>
            indir.mutate({
              yol: '/api/actions/devir/template',
              yedekAd: 'dof-devir-sablonu.xlsx',
            })
          }
        >
          Şablonu indir
        </Button>
        <FileInput
          data-testid="devir-dosya"
          placeholder="Doldurulmuş şablonu seçin (.xlsx / .csv)"
          accept=".xlsx,.csv"
          value={dosya}
          onChange={(secilen) => {
            setDosya(secilen);
            yukle.reset();
          }}
          clearable
          size="xs"
          w={300}
        />
        <Button
          size="xs"
          data-testid="devir-yukle"
          disabled={!dosya}
          loading={yukle.isPending}
          onClick={() => dosya && yukle.mutate({ dosya })}
        >
          Yükle
        </Button>
      </Group>
      {yukle.isError && (
        <Alert color={ANLAM.engel} role="alert" data-testid="devir-yukleme-hata">
          {yukle.error.message}
        </Alert>
      )}
      {yukle.data && (
        <Stack gap="xs">
          <Group gap="xs" data-testid="devir-ozeti">
            <Badge color={ANLAM.olumlu} variant="light">
              Açık: {yukle.data.acikEklenen}
            </Badge>
            <Badge color={ANLAM.bilgi} variant="light">
              Kapalı (beyan): {yukle.data.kapaliEklenen}
            </Badge>
            <Badge color={ANLAM.engel} variant="light">
              İşlenemedi: {yukle.data.hatali}
            </Badge>
          </Group>
          <Table striped data-testid="devir-sonuc-tablosu">
            <Table.Thead>
              <Table.Tr>
                <Table.Th w={70}>Satır</Table.Th>
                <Table.Th w={220}>Sonuç</Table.Th>
                <Table.Th>Açıklama</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {yukle.data.satirlar.map((satir) => (
                <Table.Tr key={satir.satir} data-testid="devir-sonuc-satiri">
                  <Table.Td>{satir.satir}</Table.Td>
                  <Table.Td>
                    <Badge
                      variant="light"
                      color={
                        satir.sonuc === 'hata'
                          ? ANLAM.engel
                          : satir.uyarilar.length > 0
                            ? ANLAM.dikkat
                            : ANLAM.olumlu
                      }
                    >
                      {AKSIYON_DEVIR_SONUC_ETIKETI[satir.sonuc]}
                    </Badge>
                  </Table.Td>
                  <Table.Td>
                    <Stack gap={2}>
                      {satir.mesaj && <Text size="sm">{satir.mesaj}</Text>}
                      {satir.uyarilar.map((uyari) => (
                        <Text key={uyari} size="xs" c="dimmed" data-testid="devir-uyarisi">
                          {uyari}
                        </Text>
                      ))}
                    </Stack>
                  </Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
        </Stack>
      )}
    </Stack>
  );
}
